import { useTranslation } from 'react-i18next'
import { FormProvider } from 'react-hook-form'
import { Button, H2, LoadingDots, Span, Text } from '@/components/atoms'
import {
  ActionModal,
  IdCard,
  Stepper,
  StickyFooter,
} from '@/components/molecules'
import type { StepperItem } from '@/components/molecules'
import { rentersDefaultIdCardImage } from '@/shared/assets'
import {
  CARD_FLIP_PERSPECTIVE,
  CARD_FLIP_TRANSFORM,
} from '@/shared/constants/claims'
import { useClaimsRentersPage } from './page.claims.renters.hook'
import { ClaimsRentersStepOnePage } from './step-one/page.claims.renters-step-one'
import { ClaimsRentersStepTwoPage } from './step-two/page.claims.renters-step-two'

export function ClaimsRentersPage() {
  const { t } = useTranslation()
  const {
    formMethods,
    currentStep,
    isFlipping,
    isLastStep,
    hasSelectedPolicy,
    card,
    isDataLoading,
    isSubmitting,
    hasSubmitError,
    isOffline,
    isSuccessOpen,
    handleNext,
    handleBack,
    handleCancel,
    handleSuccessClose,
  } = useClaimsRentersPage()

  const steps: StepperItem[] = [
    {
      label: t('claims.renters.stepOne.label'),
      children: <ClaimsRentersStepOnePage />,
    },
    {
      label: t('claims.renters.stepTwo.label'),
      children: (
        <ClaimsRentersStepTwoPage
          hasSubmitError={hasSubmitError}
          isOffline={isOffline}
        />
      ),
    },
  ]

  const idCards = [
    <IdCard
      key="with-photo"
      variant="with-photo"
      photoUrl={rentersDefaultIdCardImage}
      hidePhotoBackground
      planName={card.planName}
      ownerName={card.ownerName}
      policyId={card.policyId}
      startDate={card.startDate}
      className="hidden max-w-83.5 md:flex"
    />,
    <IdCard
      key="no-photo"
      variant="no-photo"
      policyId={card.policyId}
      address={card.address}
      className="hidden max-w-83.5 md:flex"
    />,
  ]

  if (isDataLoading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-white">
        <LoadingDots className="loading-xl" />
      </div>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <div className="flex min-h-dvh flex-col bg-white">
        {/* overflow-clip (not hidden): a hidden container is still scrollable, and
          focusing an input made the browser scroll it towards the off-screen step */}
        <div className="mx-auto flex w-full max-w-266.5 flex-1 flex-col overflow-clip px-6 pt-6 pb-0 md:flex-row md:gap-8 md:px-0 md:pt-8 md:pb-32">
          <div className="z-10 hidden flex-col bg-white md:flex md:max-w-117 md:justify-start">
            {/* Grid rows 0fr → 1fr animates the height, so the title below slides down smoothly */}
            <div
              className={`grid transition-[grid-template-rows] duration-500 ease-out ${hasSelectedPolicy ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="min-h-0 overflow-hidden">
                <div
                  className={`pb-6 transition duration-500 ease-out ${hasSelectedPolicy ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
                >
                  <div
                    className="transition-transform duration-300"
                    style={{
                      perspective: CARD_FLIP_PERSPECTIVE,
                      transformStyle: 'preserve-3d',
                      transform: isFlipping
                        ? CARD_FLIP_TRANSFORM.FLIPPING
                        : CARD_FLIP_TRANSFORM.IDLE,
                    }}
                  >
                    {idCards[currentStep]}
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden flex-col gap-6 md:flex">
              <H2 className="text-[3.125rem]! leading-15!">
                <Span className="text-neutral-900" textSize="inherit">
                  {t('claims.renters.titleStart')}
                </Span>
                <br />
                <Span className="text-indigo-base" textSize="inherit">
                  {t('claims.renters.titleHighlight')}
                </Span>
              </H2>
              <Text className="text-neutral-900">
                {t('claims.renters.description')}
              </Text>
            </div>
          </div>
          <div className="flex flex-1 flex-col">
            <Stepper
              items={steps}
              currentStep={currentStep}
              dynamicHeight
              className="md:gap-8"
            />
          </div>
        </div>

        <StickyFooter>
          {isLastStep ? (
            <>
              <Button
                variant="secondary"
                onClick={handleBack}
                className="h-12 w-1/2 px-2! md:w-auto md:px-8!"
                data-testid="renters-claim-back"
              >
                {t('claims.renters.back')}
              </Button>
              <Button
                onClick={handleNext}
                loading={isSubmitting}
                disabled={isSubmitting || isOffline}
                className="h-12 w-1/2 md:w-auto"
                data-testid="renters-claim-submit"
              >
                {t('claims.renters.form.submit')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={handleCancel}
                className="h-12 w-1/2 px-2! md:w-auto md:px-8!"
                data-testid="renters-claim-cancel"
              >
                {t('claims.renters.cancel')}
              </Button>
              <Button
                onClick={handleNext}
                className="h-12 w-1/2 md:w-auto"
                data-testid="renters-claim-continue"
              >
                {t('claims.renters.continue')}
              </Button>
            </>
          )}
        </StickyFooter>
      </div>

      <ActionModal
        isOpen={isSuccessOpen}
        variant="success"
        title={t('claims.renters.success.title')}
        description={t('claims.renters.success.description')}
        primaryAction={{
          label: t('claims.renters.success.action'),
          onClick: handleSuccessClose,
        }}
        onClose={handleSuccessClose}
      />
    </FormProvider>
  )
}
